import {
  Connectable,
  connectable,
  forkJoin as observableForkJoin,
  Observable,
  of as observableOf,
  ReplaySubject,
  Subscription,
  throwError as observableThrowError,
} from 'rxjs';

import {catchError, first, map, mergeMap, shareReplay} from 'rxjs/operators';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {SmdPaginationModel, SmdPaginatorComponent} from '../smd-paginator';
import {ResourceService} from '../../../resource/resource.service';
import {SearchLimitation} from '../../../resource/filterresult';
import {MatSnackBar} from '@angular/material/snack-bar';
import {LayoutService} from '../../../layout/layout.service';
import {SearchScopeConfiguration} from '../../../cache/uiconfiguration/searchscopeconfiguration';
import {CacheService} from '../../../cache/cache.service';
import {DisplayAttribute} from '../../../resource/displayattribute';
import {LanguageService} from '../../../language/language.service';
import {AddViewEvent} from '../../../resource/events/addviewevent';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';
import {SmdTimelineRowComponent} from './timeline-row.component';
import {ActionBarConfiguration} from '../../../cache/uiconfiguration/actionbarconfiguration';
import {SmdFilter} from '../smd-parts/smd-filter.component';

@Component({
  selector: 'smd-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SmdTimeline implements AfterViewInit, OnDestroy {
  languageResource: Observable<string>;

  rows: any[] = [];
  visibleRows: any[] = [];
  loadTimestamp: Date = null;
  loadingTime: number = null;

  @ViewChild(SmdPaginatorComponent, {static: true}) paginatorComponent: SmdPaginatorComponent;
  @ViewChild(SmdFilter) filterComponent: SmdFilter;

  @ViewChildren('timelineRow') rowComponents: QueryList<SmdTimelineRowComponent>;

  searchScopeConfigs: SearchScopeConfiguration[] = null;
  selectedSearchScope: SearchScopeConfiguration;

  filter: string = null;
  filterValue: string;
  attributes: string;
  index: number = 0;
  models: any[] = [];
  @Input() paginated: boolean = true;
  @Input() title: string = null;
  @Input() additionalLoadedAttributes: string[] = null;
  @Input() showLoadingTime: boolean = false;

  @Input()
  set defaultPageSize(strVal: string) {
    const ps: number = Number(strVal);

    if (ps == null || ps < 1) return;
    let ranges: number[] = [10, 25, 50, 100];
    if (ranges.indexOf(ps) === -1) {
      ranges.push(ps);
      ranges = ranges.sort((a, b) => a - b);
    }
    this.paginatorComponent.ranges = ranges;
    this.paginatorComponent.defaultRange = ps;
    this.paginatorComponent.selectedRange = ps;
  }

  private sortColumn: string = null;
  private sortAscending: boolean = true;
  private resourceSubscription: Subscription = null;

  private totalCount: number = 0;
  displayedTotalCount: number = 0;
  loading: boolean = true;
  hideTarget: boolean = false;

  @Output() onAddView: EventEmitter<AddViewEvent> = new EventEmitter<AddViewEvent>();
  @Output() onCloseView: EventEmitter<{}> = new EventEmitter<{}>();
  @Output() onRowsReloaded: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() onError: EventEmitter<any> = new EventEmitter<any>();

  private readonly _htmlElement: HTMLElement;

  constructor(
    private resourceService: ResourceService,
    private layoutService: LayoutService,
    private cacheService: CacheService,
    elementRef: ElementRef,
    private languageService: LanguageService,
    public changeDetector: ChangeDetectorRef,
    private envConfig: EnvironmentConfigService,
    private snackBar: MatSnackBar,
  ) {
    this.languageResource = this.cacheService.getLanguageResource();
    this._htmlElement = elementRef.nativeElement;
  }

  private lastLimitationNotificationFilter: string = null;

  // informs the user once per search text that the search had to be limited (phases, attributes
  // or terms) to keep every request below the maximum query string length
  private notifySearchLimitation(limitation: SearchLimitation): void {
    if (this.filterValue === this.lastLimitationNotificationFilter) return;
    this.lastLimitationNotificationFilter = this.filterValue;
    this.languageResource.pipe(first()).subscribe(lr => {
      const message = ResourceService.searchLimitationMessage(limitation, lr);
      if (message != null) {
        this.snackBar.open(message, null, {duration: 8000});
      }
    });
  }

  enableFilter: boolean = false;
  enableFilterbuilder: boolean = false;
  showExpandAllButton: boolean = false;
  showCollapseAllButton: boolean = false;

  private observer: MutationObserver;

  observableCache: Connectable<Observable<any>>[] = [];
  mgmPolicyObservableCache: Connectable<Observable<any>>[] = [];
  observableConnections: Subscription[] = [];

  ngOnDestroy(): void {
    this.observableConnections.forEach(s => s.unsubscribe());
  }

  ngAfterViewInit(): void {
    this.onResize();

    const self = this;
    this.observer = new MutationObserver(mutations => {
      mutations.forEach(function (mutation) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'hidden' &&
          mutation.target['hidden'] == false
        ) {
          self.onResize();
        }
      });
    });
    this.observer.observe(this._htmlElement, {
      attributes: true,
      childList: true,
      characterData: true,
    });
  }

  @HostListener('window:resize', ['$event.target'])
  onResize(): void {
    if (this._htmlElement.hidden) return;

    this.changeDetector.markForCheck();

    if (this._htmlElement.clientWidth < 500) {
      this._htmlElement.classList.add('verySmallView');
      this._htmlElement.classList.add('smallView');
    } else if (this._htmlElement.clientWidth < 900) {
      this._htmlElement.classList.remove('verySmallView');
      this._htmlElement.classList.add('smallView');
    } else {
      this._htmlElement.classList.remove('verySmallView');
      this._htmlElement.classList.remove('smallView');
    }

    if (this._htmlElement.clientWidth < 575) {
      const footers = this._htmlElement.querySelectorAll('.smd-data-table-footer');
      footers.forEach(footer => footer.classList.add('small-footer'));
    } else {
      const footers = this._htmlElement.querySelectorAll('.smd-data-table-footer');
      footers.forEach(footer => footer.classList.remove('small-footer'));
    }
  }

  refreshData(): void {
    this.loadTimestamp = null;
    this.fetchResources();
  }

  public reloadList(
    searchScopeConfigurations: SearchScopeConfiguration[],
    hideTarget: boolean = false,
    defaultSearchScope?: SearchScopeConfiguration,
  ): void {
    this.loading = true;
    this.hideTarget = hideTarget;
    this.searchScopeConfigs = searchScopeConfigurations;
    this.visibleRows.length = 0;
    this.filterComponent?.resetCustomFilter();
    this.onResize();
    this.showExpandAllButton = false;
    this.showCollapseAllButton = false;
    if (this.searchScopeConfigs != null && this.searchScopeConfigs.length > 0) {
      this.searchScopeConfigs = this.searchScopeConfigs.sort(
        (a: SearchScopeConfiguration, b: SearchScopeConfiguration) => {
          return a.order - b.order;
        },
      );
      if (defaultSearchScope != null) {
        this.selectedSearchScope = defaultSearchScope;
      } else {
        this.selectedSearchScope = this.searchScopeConfigs[0];
      }

      if (this.selectedSearchScope?.id === 'custom') {
        this.showExpandAllButton = true;
      }
    } else {
      this.selectedSearchScope = null;
    }
    this.refreshSearchScope();
    this.onResize();
  }

  @Input()
  set actionBarConfigs(confs: ActionBarConfiguration[]) {
    this.enableFilter = false;
    this.enableFilterbuilder = false;
    if (confs == null) return;
    confs.forEach((conf: ActionBarConfiguration) => {
      if (conf != null) {
        if (conf.displayname === 'defaultFilter') {
          this.enableFilter = true;
        } else if (conf.displayname === 'defaultAdvancedFilter') {
          this.enableFilterbuilder = true;
        }
      }
    });
    this.changeDetector.markForCheck();
  }

  refreshSearchScope() {
    this.paginatorComponent.currentPage = 1;
    this.index = 0;
    this.filterValue = '';
    this.sortColumn = null;
    this.lastLimitationNotificationFilter = null;
    let scope: SearchScopeConfiguration = this.selectedSearchScope;
    if (scope != null) {
      this.sortColumn = scope.sortingattribute;
      this.sortAscending = scope.sortdirection !== 'desc';

      let displayColumns: string[];
      if (Array.isArray(scope.attributedisplayed)) {
        displayColumns = scope.attributedisplayed;
      } else if (scope.attributedisplayed != null) {
        displayColumns = scope.attributedisplayed.split(';');
      } else {
        displayColumns = [];
      }
      displayColumns = displayColumns.map(s => s.trim()).filter(s => s !== '');
      if (displayColumns.length === 0) {
        displayColumns.push(
          this.envConfig.getAttributeName('DisplayName'),
          this.envConfig.getAttributeName('Description'),
          this.envConfig.getAttributeName('ObjectType'),
        );
      }

      let attributes: string[] = displayColumns;
      if (this.additionalLoadedAttributes != null && this.additionalLoadedAttributes.length !== 0) {
        attributes = Array.from(new Set(attributes.concat(this.additionalLoadedAttributes)));
      }

      if (scope.xpath == null) {
        console.error('Filter is null, cannot display search result.');
        this.loading = false;
        this.onResize();
      } else {
        this.attributes = attributes.join(',');
        this.filter = scope.xpath;
        this.fetchResources(scope.xpath, this.attributes);
      }
    } else {
      this.models.length = 0;
      this.rows = [];
    }
  }

  fetchResources(filter: string = this.filter, attributes: string = this.attributes) {
    if (filter == null || attributes == null) {
      this.loading = false;
      this._updateRows();
      this.onResize();
      return;
    }
    this.loadingTime = null;
    const startTime = Date.now();

    this.layoutService.updatePreloaderState('active');

    if (this.resourceSubscription != null) {
      if (!this.resourceSubscription.closed) {
        this.layoutService.updatePreloaderState('hide');
      }
      this.resourceSubscription.unsubscribe();
    }

    let pageSize: number = 0;
    if (this.paginatorComponent != null) {
      pageSize += this.paginatorComponent.selectedRange;
    }

    let sortColumn: string = this.sortColumn;
    let sortAsc: boolean = this.sortAscending;
    let index: number = this.index;

    this.resourceSubscription = this.resourceService
      .searchAndFilterResources(
        filter,
        this.filterValue,
        this.selectedSearchScope.attributesearched ?? [],
        attributes,
        pageSize,
        index,
        sortColumn,
        sortAsc,
      )
      .pipe(
        map(res => {
          this.loadTimestamp = new Date();
          const endTime = Date.now();
          this.loadingTime = endTime - startTime;
          if (res == null) {
            this.totalCount = 0;
            this.displayedTotalCount = 0;
            this.paginatorComponent.numPages = 1;
            this.models = [];
          } else {
            this.totalCount = res.TotalCount;
            this.models = res.Results;
          }
          return res;
        }),
        catchError((error, caught) => this.handleError(error, caught)),
      )
      .subscribe(
        res => {
          this._updateRows();
          if (res === null || res.Finished === true) {
            this.layoutService.updatePreloaderState('hide');
            this.loading = false;
            if (res != null && res.Limitation != null) {
              this.notifySearchLimitation(res.Limitation);
            }
          }
          this.onResize();
        },
        () => {
          this.totalCount = 0;
          this.displayedTotalCount = 0;
          this.paginatorComponent.numPages = 1;
          this.models = [];
          this._updateRows();
          this.layoutService.updatePreloaderState('hide');
          this.loading = false;
          this.onResize();
        },
      );
  }

  private createRemoteRowModels(): void {
    this.rows = [];
    this.models.forEach((model: any, index: number) => {
      this.createRemoteRowModel(model, index);
    });
  }

  private createRemoteRowModel(model: any, index?: number) {
    if (model == null) {
      throw 'DataTable: Model is null in createRemoteRowModel';
    }
    if (index != null) {
      this.rows[index] = model;
    } else {
      this.rows.push(model);
    }
  }

  _updateRows() {
    if (this.models) {
      this.createRemoteRowModels();
    }
    this._updateVisibleRows();
    this._updateTotalCount();
  }

  _onFilter(event: any): void {
    this.index = 0;
    this.paginatorComponent.currentPage = 1;
    this.refreshData();
  }

  _onPageChange(paginationModel: SmdPaginationModel) {
    if (paginationModel == null) return;
    this.index = (paginationModel.page - 1) * paginationModel.size;
    if (this.index < 0) {
      this.index = 0;
      this.paginatorComponent.currentPage = 1;
    } else if (this.index >= this.displayedTotalCount && this.displayedTotalCount > 0) {
      this.paginatorComponent.numPages = Math.ceil(
        this.displayedTotalCount / this.paginatorComponent.selectedRange,
      );
      this.paginatorComponent.currentPage = this.paginatorComponent.numPages;
      this.index = (this.paginatorComponent.currentPage - 1) * paginationModel.size;
    }
    this.refreshData();
  }

  private _updateVisibleRows() {
    this.visibleRows = this.rows.slice();
    if (this.index !== 0 && this.visibleRows.length === 0) {
      this._onPageChange(
        new SmdPaginationModel(
          this.paginatorComponent.currentPage,
          this.paginatorComponent.selectedRange,
        ),
      );
      return;
    }
    this.onRowsReloaded.emit(this.visibleRows);
  }

  private _updateTotalCount() {
    this.displayedTotalCount = this.totalCount;

    if (this.paginatorComponent != null) {
      this.paginatorComponent.numPages = Math.ceil(
        this.displayedTotalCount / this.paginatorComponent.selectedRange,
      );
    }
  }

  protected expandAll(): void {
    this.rowComponents?.forEach(row => {
      row.expansionPanel.open();
    });
    this.showExpandAllButton = false;
    this.showCollapseAllButton = true;
  }

  protected collapseAll(): void {
    this.rowComponents?.forEach(row => {
      row.expansionPanel.close();
    });
    this.showExpandAllButton = true;
    this.showCollapseAllButton = false;
  }

  private cacheCounter = 0;
  private cacheCounterResetTimer = null;

  private loadCachedBasicResource(guid: string, loadFromBackend = true): Observable<any> {
    let valueObs = this.observableCache[guid];
    if (loadFromBackend && valueObs == null) {
      let requestObs = this.resourceService.getBasicResource(guid);
      if (this.cacheCounter < 200) {
        valueObs = connectable(requestObs, {
          connector: () => new ReplaySubject(1),
          resetOnDisconnect: false,
        });
        this.observableConnections.push(valueObs.connect());
      } else {
        valueObs = requestObs.pipe(shareReplay(1));
      }
      this.observableCache[guid] = valueObs;
      this.cacheCounter++;
      if (this.cacheCounterResetTimer === null) {
        this.cacheCounterResetTimer = setTimeout(() => {
          this.cacheCounter = 0;
          this.cacheCounterResetTimer = null;
        }, 10000);
      }
    }
    return valueObs;
  }

  displayNameForResource(guid: string, loadFromBackend = true): Observable<string> {
    return this.loadCachedBasicResource(guid, loadFromBackend).pipe(
      map(r => (r == null ? null : r[this.envConfig.getAttributeName('DisplayName')])),
      mergeMap(dn => {
        if (dn == null) {
          return observableForkJoin(
            this.cacheService.getLanguageResource().pipe(first()),
            this.cacheService
              .getDisplayAttribute(
                this.envConfig.getAttributeName('DisplayName'),
                this.envConfig.getObjectTypeName('Resource'),
              )
              .pipe(first()),
            (lr: any, da: DisplayAttribute) => {
              return (
                '⟨ ' +
                LanguageService.translate('DatatableNo', 'No', lr) +
                ' ' +
                (da?.Caption ?? 'DisplayName') +
                ' ⟩'
              );
            },
          );
        } else {
          return observableOf(dn);
        }
      }),
    );
  }

  objectTypeForResource(guid: string): Observable<string> {
    return this.loadCachedBasicResource(guid).pipe(
      map(x => (x == null ? null : x[this.envConfig.getAttributeName('ObjectType')])),
    );
  }

  formatNumber(number: Number, options?: Intl.NumberFormatOptions): Observable<string> {
    return this.languageService
      .getLanguage(this.cacheService.getSupportedLocaleConfiguration())
      .pipe(
        map((language: string) => {
          return number.toLocaleString(language, options);
        }),
      );
  }

  getCaughtObservable(obs: Observable<any>): Observable<any> {
    if (obs == null) return null;
    return obs.pipe(catchError((error, caught) => this.handleAsyncError(error, caught)));
  }

  handleAsyncError(error: any, caught?: Observable<any>) {
    if (this.onError != null) {
      this.onError.emit(error);
    }
    return observableOf(null);
  }

  handleError(error: any, caught?: Observable<any>) {
    if (this.onError != null) {
      this.onError.emit(error);
    }
    return observableThrowError(error);
  }
}
